import { EventBus, EventBusEvent } from './EventBus';

/**
 *
 * @typedef {import('./DatasetManager').BrowseOptions} BrowseOptions
 * @typedef {import('./DatasetManager').BrowsePayload} BrowsePayload
 * @typedef {import('./DatasetManager').BrowseHandler} BrowseHandler
 * @typedef {import('./DatasetManager').BrowseResolver} BrowseResolver
 * @typedef {import('./DatasetManager').PickOptions} PickOptions
 * @typedef {import('./DatasetManager').PickPayload} PickPayload
 * @typedef {import('./DatasetManager').PickHandler} PickHandler
 * @typedef {import('./DatasetManager').PickResolver} PickResolver
 */
let datasetManager = null;
const datasetManagerEnforcer = Symbol('datasetManagerEnforcer');
const eventBusInstance = new EventBus();

const DatasetManagerEvent = {
    BROWSE: 'browse',
    PICK: 'pick',
    CANCEL: 'cancel',
    NOTIFY: 'notify'
};

export const DatasetManagerNotifySubject = {
    DATASET_USAGE: 'dataset-usage',
};

export default class DatasetManager {
    /**
     * Constructor
     *
     * @param {symbol} enforcer  singleton enforcer
     */
    constructor(enforcer) {
        if (enforcer !== datasetManagerEnforcer) {
            throw new Error(`Instantiation failed: use DatasetManager.instance`);
        }
    }

    /**
     * @return {DatasetManager}
     */
    static get instance() {
        if (!datasetManager) {
            datasetManager = new DatasetManager(datasetManagerEnforcer);
        }
        return datasetManager;
    }

    /**
     * Invokes env dataset manager's browse method for dataset edition
     *
     * @param {BrowseOptions} options
     * @return {Promise<BrowseResolverPayload|null>}
     */
    browse(options) {
        return new Promise((resolve) => {
            /** @type {BrowsePayload} */
            const payload = { options, resolve };
            eventBusInstance.trigger(new EventBusEvent(DatasetManagerEvent.BROWSE), payload);
        });
    }

    /**
     * Invokes env datasets manager's pick method for dataset picking
     *
     * @param {PickOptions} options
     * @return {Promise<PickResolverPayload|null>}
     */
    pick(options) {
        return new Promise((resolve) => {
            /** @type {PickPayload} */
            const payload = { options, resolve };
            eventBusInstance.trigger(new EventBusEvent(DatasetManagerEvent.PICK), payload);
        });
    }

    /**
     * Invokes env datasets manager's cancel method for dataset picking or browsing
     *
     * @param {PickOptions} options
     * @return {Promise<any|null>}
     */
    cancel(options = null) {
        return new Promise((resolve) => {
            const payload = { options, resolve };
            eventBusInstance.trigger(new EventBusEvent(DatasetManagerEvent.CLOSE), payload);
        });
    }

    /**
     * @param {string} type
     * @param {Record<string, any>} details
     */
    notify(type, details) {
        type = `${DatasetManagerEvent.NOTIFY}:${type}`;
        eventBusInstance.trigger(new EventBusEvent(type), details);
    }

    /**
     * Registers a browse() observer (used by the env)
     *
     * @param {BrowseHandler} handler     browse handler invoked by @see browse()
     * @return {Function}                 dispose function to unregister observer
     */
    onBrowse(handler) {
        const event = new EventBusEvent(DatasetManagerEvent.BROWSE);
        /**
         * @param {EventBusEvent} event
         * @param {BrowsePayload} payload
         */
        const eventHandler = (event, payload) => {
            handler(payload);
        };
        return eventBusInstance.listen(event, eventHandler);
    }

    /**
     * Registers a pick() observer (used by the env)
     *
     * @param {PickHandler} handler     pick handler invoked by @see pick()
     * @return {Function}               dispose function to unregister observer
     */
    onPick(handler) {
        const event = new EventBusEvent(DatasetManagerEvent.PICK);
        /**
         * @param {EventBusEvent} event
         * @param {PickPayload} payload
         */
        const eventHandler = (event, payload) => {
            handler(payload);
        };
        return eventBusInstance.listen(event, eventHandler);
    }

    /**
     * Registers a cancel() observer (used by the env)
     *
     * @param {PickHandler} handler     cancel handler invoked by @see cancel()
     * @return {Function}               dispose function to unregister observer
     */
    onCancel(handler) {
        const event = new EventBusEvent(DatasetManagerEvent.CLOSE);
        /**
         * @param {EventBusEvent} event
         * @param {PickPayload} payload
         */
        const eventHandler = (event, payload) => {
            handler(payload);
        };
        return eventBusInstance.listen(event, eventHandler);
    }

    /**
     * @param {string} type
     * @param {function(details: Record<string, string>): void} handler
     * @return {function(): void}
     */
    onNotify(type, handler) {
        type = `${DatasetManagerEvent.NOTIFY}:${type}`;
        const event = new EventBusEvent(type);
        const eventHandler = (event, payload) => {
            handler(payload);
        };
        return eventBusInstance.listen(event, eventHandler);
    }
}
